import React, { createContext, useContext, useEffect, useState } from "react";

// Light/Dark theme for the register — a per-browser preference (localStorage), mirroring the POS
// device-local "DarkMode" setting. Components read tokens via useRegTheme(); the topbar toggle flips
// the whole register at once. Constant across themes: header navy, accent blue, OOS orange, delete red.
const STORAGE_KEY = "qv_web_register_theme";

export const THEMES = {
  // Warm "cream/sepia" light theme — soft on the eyes (no eye-searing pure white). Backgrounds are
  // warm ivory/sand; text is a warm near-black; the blue accent + navy header stay for brand parity.
  light: {
    mode: "light",
    appBg: "#EDE6D8", // warm sand page
    headerBg: "#0F172A",
    headerText: "#FFFFFF",
    surface: "#FAF5EB", // warm ivory panels
    surfaceAlt: "#F3ECDC",
    card: "#FAF5EB",
    cardBorder: "#E4D9C3",
    text: "#33302A", // warm near-black
    textMuted: "#7A7160",
    price: "#0A64F9",
    divider: "#E4D9C3",
    dividerSoft: "#EEE6D5",
    inputBg: "#FCF8F0",
    inputBorder: "#D8CCB4",
    inputText: "#33302A",
    chipBg: "#FCF8F0",
    chipBorder: "#D8CCB4",
    chipText: "#4A4438",
    chipActiveBg: "#F3E8CE", // warm selected state, blue border keeps the selection cue
    chipActiveBorder: "#0A64F9",
    chipActiveText: "#0A4FB8",
    accent: "#0A64F9",
  },
  dark: {
    mode: "dark",
    appBg: "#0b1220",
    headerBg: "#0f172a",
    headerText: "#FFFFFF",
    surface: "#0b1220",
    surfaceAlt: "#0f1a2f",
    card: "#0f1a2f",
    cardBorder: "#1f2c47",
    text: "#E5EDF7",
    textMuted: "#94A3B8",
    price: "#4C8DFF",
    divider: "#1f2c47",
    dividerSoft: "#16233c",
    inputBg: "#0f1b30",
    inputBorder: "#25324d",
    inputText: "#E5EDF7",
    chipBg: "#0f1b30",
    chipBorder: "#25324d",
    chipText: "#CBD5E1",
    chipActiveBg: "#3b4a63",
    chipActiveBorder: "#64748B",
    chipActiveText: "#FFFFFF",
    accent: "#2563EB",
  },
};

const RegThemeContext = createContext(THEMES.dark);
export { RegThemeContext };
export const useRegTheme = () => useContext(RegThemeContext);

function readInitialMode() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark") return v;
  } catch (e) {
    /* localStorage unavailable */
  }
  return "dark"; // default Dark (matches the POS screenshots)
}

// Owns the theme state + persistence. Used once by the register root.
export function useThemeController() {
  const [mode, setMode] = useState(readInitialMode);
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch (e) {
      /* ignore */
    }
  }, [mode]);
  const toggle = () => setMode((m) => (m === "dark" ? "light" : "dark"));
  return { mode, toggle, tokens: THEMES[mode] };
}

export function ThemeToggle({ mode, onToggle }) {
  return (
    <button
      className="qv-btn qv-btn--ghost qv-btn--icon"
      onClick={onToggle}
      title={mode === "dark" ? "Switch to light mode" : "Switch to dark mode"}
      aria-label={mode === "dark" ? "Switch to light mode" : "Switch to dark mode"}
    >
      {mode === "dark" ? "☀" : "☾"}
    </button>
  );
}
